import { memo, useEffect, useState } from 'react'
import {
  getAssetUrl,
  getLibraryThumbnailUrl,
  getVideoPosterUrl,
} from '../../../../api/useAssetUrl'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import { createNadDebug } from '../../../../utils/debugLog'
import { stripUrlQuery } from '../../../../utils/stripUrlQuery'

const videoReferenceThumbnailLog = createNadDebug('dw:video-reference-thumbnail:preview')

export type VideoReferenceThumbnailSurface =
  | 'config-reference-row'
  | 'quick-info-reference'
  | 'reference-row'
  | 'video-editor-reference'
  | 'node-selector-grid'
  | 'selected-reference-summary'

export interface VideoReferenceThumbnailProps {
  projectId: string | null | undefined
  nodeId: string
  assetPath: string
  alt: string
  className: string
  surface: VideoReferenceThumbnailSurface
  cacheBust?: string
  isLibrary?: boolean
  loading?: 'eager' | 'lazy'
}

/**
 * First-frame image preview for a persisted video reference.
 *
 * Project videos use the derived `.poster.jpg` asset exposed by `?poster=1`.
 * Library and absolute upload URLs do not have that sibling, while old project
 * videos can predate poster generation, so those cases explicitly fall back to
 * the video element instead of ever feeding video bytes to an `<img>`.
 */
const VideoReferenceThumbnail = memo(function VideoReferenceThumbnail({
  projectId,
  nodeId,
  assetPath,
  alt,
  className,
  surface,
  cacheBust,
  isLibrary = false,
  loading = 'lazy',
}: VideoReferenceThumbnailProps) {
  const isAbsolute = assetPath.startsWith('http://') || assetPath.startsWith('https://')
  const sourceVideoPathForLog = stripUrlQuery(assetPath)
  const videoUrl = isLibrary
    ? getLibraryThumbnailUrl(assetPath)
    : projectId || isAbsolute
      ? getAssetUrl(projectId ?? '', assetPath, 0, cacheBust)
      : null
  const posterUrl = isLibrary || !projectId
    ? null
    : getVideoPosterUrl(projectId, assetPath, cacheBust)
  // Subscribe to auth refreshes so both the video and poster URLs are rebuilt
  // with the current cached token. The hook is intentionally a pass-through.
  const authenticatedVideoUrl = useAuthenticatedMediaUrl(videoUrl)
  const [posterFailed, setPosterFailed] = useState(false)

  useEffect(() => {
    setPosterFailed(false)
    if (!posterUrl) {
      videoReferenceThumbnailLog({
        event: 'video-reference-thumbnail-fallback',
        trace_id: `video-reference-thumbnail:${nodeId}`,
        reference_node_id: nodeId,
        preview_surface: surface,
        source_video_path: sourceVideoPathForLog,
        preview_kind: 'video-fallback',
        element_type: 'video',
        reason: isLibrary
          ? 'library-asset'
          : projectId
            ? 'poster-url-not-derivable'
            : 'missing-project-id',
        fallback: 'video',
      })
    }
  }, [isLibrary, nodeId, posterUrl, projectId, sourceVideoPathForLog, surface])

  if (posterUrl && !posterFailed) {
    return (
      <img
        src={posterUrl}
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        data-testid="video-reference-poster"
        data-reference-node-id={nodeId}
        data-reference-surface={surface}
        onLoad={() => {
          videoReferenceThumbnailLog({
            event: 'video-reference-thumbnail-image-loaded',
            trace_id: `video-reference-thumbnail:${nodeId}`,
            reference_node_id: nodeId,
            preview_surface: surface,
            source_video_path: sourceVideoPathForLog,
            preview_kind: 'thumbnail-image',
            element_type: 'img',
          })
        }}
        onError={() => {
          videoReferenceThumbnailLog({
            event: 'video-reference-thumbnail-fallback',
            trace_id: `video-reference-thumbnail:${nodeId}`,
            reference_node_id: nodeId,
            preview_surface: surface,
            source_video_path: sourceVideoPathForLog,
            preview_kind: 'video-fallback',
            element_type: 'video',
            reason: 'poster-load-error',
            fallback: 'video',
          })
          setPosterFailed(true)
        }}
      />
    )
  }

  if (!authenticatedVideoUrl) return null

  return (
    <video
      src={authenticatedVideoUrl}
      aria-label={alt}
      className={className}
      preload="metadata"
      muted
      playsInline
      data-testid="video-reference-fallback"
      data-reference-node-id={nodeId}
      data-reference-surface={surface}
    />
  )
})

VideoReferenceThumbnail.displayName = 'VideoReferenceThumbnail'

export { VideoReferenceThumbnail }
